import {
  Accordion,
  AccordionSummary,
  Box,
  Button as MuiButton,
  Tooltip,
  StepLabel,
  Stepper,
  Typography
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import UndoIcon from "@material-ui/icons/Undo";
import { Alert, PermissionGate } from "components";
import { usePopup } from "hooks/usePopup";
import React, { useEffect, useState } from "react";
import batchService from "services/batchService";
import ConfirmationPopup from "shared/ConfirmationPopup";
import { mapType } from "utils";

import { ReceiptBox } from "./stepsBody/Box";
import { Classification } from "./stepsBody/Classification";
import { QualityBlitz } from "./stepsBody/Quality";
import { ReceiptQuantity } from "./stepsBody/Quantity";
import { Tag } from "./stepsBody/Tag";
import { WeightBlitz } from "./stepsBody/Weight";
import {
  ColorlibConnector,
  ProductInfo,
  ProductText,
  Step,
  AlertDevolution,
  AlertText
} from "./style";

export default function ReceiptSteps({
  batchData,
  blitzData,
  hasBlitz,
  handleClose,
  setTitle
}) {
  const { purchase_type } = batchData.product;
  const isWeight = purchase_type === 1;
  const workflow = batchData?.workflow;
  const workflowStep = workflow?.currentStep;
  const allowedActions = workflow?.allowedActions || [];
  const hasWorkflow = Boolean(workflowStep);
  const isRejectedWorkflow = workflowStep === "rejected";
  const shouldUseBlitzFlow = hasWorkflow ? hasBlitz : Boolean(blitzData);
  const hasMainAction =
    isRejectedWorkflow ||
    !hasWorkflow ||
    allowedActions.some(action => action !== "mark_not_delivered");

  const [activeStep, setActiveStep] = useState(getInitialStep);
  const [confirmedQty, setConfirmedQty] = useState(
    batchData.box_quantity || null
  );
  const [price, setPrice] = useState(batchData.combined_price || null);
  const [sampleQualityQty, setSampleQualityQty] = useState(
    batchData.sample_quantity || null
  );
  const [sampleLowQualityQty, setSampleLowQualityQty] = useState(
    batchData.sample_low_quantity || null
  );

  const [receiptQty, setReceiptQty] = useState(null);
  const [boxQty, setBoxQty] = useState(null);

  const [updatedBatch, setUpdatedBatch] = useState({});
  const [isApproved, setIsApproved] = useState(!isRejectedWorkflow);
  const [isResetting, setIsResetting] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);

  const { addPopup } = usePopup();
  const steps = getSteps();

  function getInitialStep() {
    if (!workflowStep) {
      return batchData?.blitz_step ?? 0;
    }

    if (isRejectedWorkflow) {
      return batchData?.refusal_reason === "peso" ? 2 : 1;
    }

    if (!shouldUseBlitzFlow) {
      return (
        {
          entry: 0,
          unloading: 1,
          received: 2
        }[workflowStep] ?? -1
      );
    }

    return (
      {
        entry: 0,
        quality_blitz: 1,
        weight_blitz: 2,
        classification: 3,
        unloading: 4,
        received: 5
      }[workflowStep] ?? -1
    );
  }

  function getSteps() {
    if (!shouldUseBlitzFlow) {
      return ["Entrada", "Inserir quantidade", "Imprimir etiqueta"];
    }

    return [
      "Entrada",
      "Blitz de qualidade",
      "Blitz do peso",
      "Inserir classificações",
      "Inserir quantidade",
      "Imprimir etiqueta"
    ];
  }

  const handleChangeSampleQualityQty = e => {
    const { value: inputValue } = e.target;
    const value = isWeight
      ? parseFloat(inputValue, 10)
      : parseInt(inputValue, 10);

    const isValid = !isNaN(value) && value >= 0;
    setSampleQualityQty(!isValid ? "" : value);
  };

  const handleChangeSampleLowQualityQty = e => {
    const { value: inputValue } = e.target;
    const value = isWeight
      ? parseFloat(inputValue, 10)
      : parseInt(inputValue, 10);

    const isValid = !isNaN(value) && value >= 0;
    setSampleLowQualityQty(!isValid ? "" : value);
  };

  const handleChangeReceiptQty = e => {
    const { value: inputValue } = e.target;
    const value = isWeight
      ? parseFloat(inputValue, 10)
      : parseInt(inputValue, 10);
    const isValid = !isNaN(value) && value >= 0;
    setReceiptQty(!isValid ? "" : value);
  };

  const handleChangeBoxQty = e => {
    const { value: inputValue } = e.target;
    const value = parseInt(inputValue, 10);
    const isValid = !isNaN(value) && value >= 0;
    setBoxQty(!isValid ? "" : value);
  };

  function getStepContent(step) {
    if (isRejectedWorkflow) {
      return (
        <RejectedReceiptContent
          refusalReason={batchData?.refusal_reason}
          handleClose={handleClose}
        />
      );
    }

    const stepBodyWithRule = {
      0: (
        <ReceiptBox
          handleNext={handleNext}
          batchData={batchData}
          blitzData={blitzData}
          confirmedQty={confirmedQty}
          setConfirmedQty={setConfirmedQty}
          handleCloseOpenSteps={handleClose}
          price={price}
          setPrice={setPrice}
        />
      ),
      1: (
        <QualityBlitz
          batchData={batchData}
          blitzData={blitzData}
          handleNext={handleNext}
          handleChangeSampleQualityQty={handleChangeSampleQualityQty}
          handleChangeSampleLowQualityQty={handleChangeSampleLowQualityQty}
          sampleQualityQty={sampleQualityQty}
          sampleLowQualityQty={sampleLowQualityQty}
          handleCloseOpenSteps={handleClose}
          isApproved={isApproved}
          setIsApproved={setIsApproved}
        />
      ),
      2: (
        <WeightBlitz
          blitzData={blitzData}
          batchData={batchData}
          handleNext={handleNext}
          handleCloseOpenSteps={handleClose}
        />
      ),
      3: (
        <Classification
          batchData={batchData}
          handleNext={handleNext}
          sampleQualityQty={sampleQualityQty}
          sampleLowQualityQty={sampleLowQualityQty}
          handleClose={handleClose}
        />
      ),
      4: (
        <ReceiptQuantity
          batchData={batchData}
          handleNext={handleNext}
          receiptQty={receiptQty}
          handleChangeReceiptQty={handleChangeReceiptQty}
          setUpdatedBatch={setUpdatedBatch}
          handleChangeBoxQty={handleChangeBoxQty}
          boxQty={boxQty}
        />
      ),
      5: <Tag handleCloseOpenSteps={handleClose} updatedBatch={updatedBatch} />
    };

    const stepBodyWithoutRule = {
      0: (
        <ReceiptBox
          handleNext={handleNext}
          batchData={batchData}
          confirmedQty={confirmedQty}
          setConfirmedQty={setConfirmedQty}
          price={price}
          setPrice={setPrice}
          handleCloseOpenSteps={handleClose}
        />
      ),
      1: (
        <ReceiptQuantity
          batchData={batchData}
          handleNext={handleNext}
          receiptQty={receiptQty}
          handleChangeReceiptQty={handleChangeReceiptQty}
          setUpdatedBatch={setUpdatedBatch}
          handleChangeBoxQty={handleChangeBoxQty}
          boxQty={boxQty}
        />
      ),
      2: <Tag handleCloseOpenSteps={handleClose} updatedBatch={updatedBatch} />
    };
    return shouldUseBlitzFlow
      ? stepBodyWithRule[step]
      : stepBodyWithoutRule[step];
  }

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const handleReset = async () => {
    setIsResetting(true);
    try {
      const response = await batchService.confirmBatch(batchData.id, {
        action: "reset"
      });
      const newBatchId = response.data?.id;
      addPopup({
        type: "success",
        title: "Recebimento resetado",
        description: `Lote #${batchData.id} cancelado. Novo lote criado: #${newBatchId}`
      });
      handleClose();
    } catch (err) {
      const msg =
        err?.response?.data?.message || "Erro ao resetar o recebimento";
      addPopup({ type: "error", title: msg });
    } finally {
      setIsResetting(false);
    }
  };

  useEffect(() => {
    const title = isRejectedWorkflow
      ? "Recebimento recusado"
      : steps[activeStep] || "Recebimento";
    setTitle(title);
  }, [activeStep, isRejectedWorkflow]);

  useEffect(() => {
    setActiveStep(getInitialStep());
  }, [batchData.id, workflowStep, shouldUseBlitzFlow, isRejectedWorkflow]);

  const stepContent = hasMainAction ? getStepContent(activeStep) : null;

  return (
    <Box style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        style={{ width: "100%", paddingTop: 0 }}
        connector={<ColorlibConnector />}
      >
        {steps.map((label, index) => {
          const hideSteps = !isApproved && index >= 2;
          return (
            <Step key={label} style={{ visibility: hideSteps && "hidden" }}>
              <Tooltip title={label} arrow placement="top">
                <StepLabel />
              </Tooltip>
            </Step>
          );
        })}
      </Stepper>

      <ConfirmationPopup
        isOpen={openResetDialog}
        onClose={() => setOpenResetDialog(false)}
        onConfirm={handleReset}
        title="Resetar recebimento"
        message={`Tem certeza que deseja resetar o recebimento do lote ${batchData.id}?`}
        description="O lote atual será cancelado e um novo lote será criado do zero com as informações originais da compra."
        confirmText="Resetar"
        cancelText="Cancelar"
        localStorageKey="hideResetReceivementConfirmation"
      />

      {batchData.seller?.has_seller_open_devolution && (
        <Box>
          <AlertDevolution>
            <AlertText className="bold">⚠️ Atenção:</AlertText>
            <AlertText>Este seller possui uma devolução em aberto</AlertText>
          </AlertDevolution>
        </Box>
      )}

      <Accordion style={{ marginBottom: "1rem" }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          style={{ background: "#F5F8F3" }}
        >
          <Typography>
            Produto: <strong>{batchData.product?.name}</strong>
          </Typography>
        </AccordionSummary>

        <ProductInfo>
          <ProductText>
            Seller: <span>{batchData.seller?.name}</span>
          </ProductText>
          <ProductText>
            Porção: <span>{mapType(batchData.product?.purchase_type)}</span>
          </ProductText>
        </ProductInfo>
      </Accordion>
      {stepContent || (
        <Typography style={{ color: "#757575", textAlign: "center" }}>
          Nenhuma ação de recebimento disponível para esta etapa.
        </Typography>
      )}

      {activeStep >= 1 && (
        <PermissionGate permissions={["Tech", "Admin", "Receivement Reset"]}>
          <Box
            display="flex"
            justifyContent="center"
            style={{ paddingTop: "0.5rem" }}
          >
            <MuiButton
              size="small"
              disabled={isResetting}
              onClick={() => setOpenResetDialog(true)}
              startIcon={<UndoIcon fontSize="small" />}
              style={{
                color: "#9e9e9e",
                textDecoration: "underline",
                textTransform: "none",
                fontSize: "0.75rem"
              }}
            >
              {isResetting ? "Resetando..." : "Resetar recebimento"}
            </MuiButton>
          </Box>
        </PermissionGate>
      )}
    </Box>
  );
}

const RejectedReceiptContent = ({ refusalReason, handleClose }) => {
  const title = refusalReason === "peso" ? "Peso recusado" : "Produto recusado";

  return (
    <>
      <Alert isSuccess={false} title={title} />

      <Box display="flex" justifyContent="flex-end">
        <MuiButton variant="contained" color="primary" onClick={handleClose}>
          Finalizar
        </MuiButton>
      </Box>
    </>
  );
};
